// Knowledge documents and shareable assets (PRD §9) + scoped retrieval (PRD §8.1).
import { z } from 'zod';
import * as K from './api-knowledge.js';
import { OkSchema } from './api.js';
import { IdParam, items, route, W, Ws } from './route-def.js';

const Doc = W({ documentId: IdParam });
const DocVersion = W({ documentId: IdParam, versionId: IdParam });
const AssetParam = W({ assetId: IdParam });
/** Binary download: the handler streams the file (attachment, nosniff); errors still use the JSON envelope. */
const Binary = z.unknown();

export const knowledgeRoutes = {
  knowledgeCapabilities: route({
    method: 'GET', path: '/workspaces/:workspaceId/knowledge/capabilities', summary: 'Capacidades de conocimiento (búsqueda semántica, límites de archivos)', tag: 'knowledge',
    minRole: 'manager', params: Ws, response: K.KnowledgeCapabilitiesSchema,
  }),
  searchKnowledge: route({
    method: 'GET', path: '/workspaces/:workspaceId/knowledge/search', summary: 'Probar búsqueda (solo fuentes publicadas del alcance)', tag: 'knowledge',
    minRole: 'manager', params: Ws, query: K.KnowledgeSearchQuerySchema, response: K.KnowledgeSearchResultSchema,
  }),

  // ─────────── Documents ───────────
  listDocuments: route({
    method: 'GET', path: '/workspaces/:workspaceId/documents', summary: 'Documentos de conocimiento', tag: 'knowledge',
    minRole: 'manager', params: Ws, query: K.DocumentListQuerySchema, response: items(K.KnowledgeDocumentSchema),
  }),
  getDocument: route({
    method: 'GET', path: '/workspaces/:workspaceId/documents/:documentId', summary: 'Documento con versiones y vista previa del texto extraído', tag: 'knowledge',
    minRole: 'manager', params: Doc, response: K.KnowledgeDocumentDetailSchema,
  }),
  createTextDocument: route({
    method: 'POST', path: '/workspaces/:workspaceId/documents', summary: 'Crear documento de texto o preguntas frecuentes', tag: 'knowledge',
    minRole: 'admin', params: Ws, body: K.CreateTextDocumentSchema, response: K.KnowledgeDocumentDetailSchema, status: 201,
  }),
  uploadDocument: route({
    method: 'POST', path: '/workspaces/:workspaceId/documents/upload', summary: 'Subir archivo (multipart/form-data, campo "file")', tag: 'knowledge',
    minRole: 'admin', params: Ws, body: K.UploadDocumentFieldsSchema, response: K.KnowledgeDocumentDetailSchema, status: 201,
  }),
  createTextDocumentVersion: route({
    method: 'POST', path: '/workspaces/:workspaceId/documents/:documentId/versions', summary: 'Nueva versión de texto o preguntas frecuentes', tag: 'knowledge',
    minRole: 'admin', params: Doc, body: K.CreateTextVersionSchema, response: K.KnowledgeDocumentDetailSchema, status: 201,
  }),
  uploadDocumentVersion: route({
    method: 'POST', path: '/workspaces/:workspaceId/documents/:documentId/versions/upload', summary: 'Nueva versión de archivo (multipart/form-data)', tag: 'knowledge',
    minRole: 'admin', params: Doc, body: K.UploadVersionFieldsSchema, response: K.KnowledgeDocumentDetailSchema, status: 201,
  }),
  publishDocumentVersion: route({
    method: 'POST', path: '/workspaces/:workspaceId/documents/:documentId/versions/:versionId/publish', summary: 'Publicar versión revisada (reemplaza la anterior)', tag: 'knowledge',
    minRole: 'admin', params: DocVersion, response: K.KnowledgeDocumentDetailSchema,
  }),
  rejectDocumentVersion: route({
    method: 'POST', path: '/workspaces/:workspaceId/documents/:documentId/versions/:versionId/reject', summary: 'Rechazar versión (queda como fallida)', tag: 'knowledge',
    minRole: 'admin', params: DocVersion, body: K.RejectVersionSchema, response: K.KnowledgeDocumentDetailSchema,
  }),
  reprocessDocumentVersion: route({
    method: 'POST', path: '/workspaces/:workspaceId/documents/:documentId/versions/:versionId/reprocess', summary: 'Volver a procesar una versión fallida', tag: 'knowledge',
    minRole: 'admin', params: DocVersion, response: K.KnowledgeDocumentDetailSchema, status: 202,
  }),
  downloadDocumentVersion: route({
    method: 'GET', path: '/workspaces/:workspaceId/documents/:documentId/versions/:versionId/file', summary: 'Descargar archivo original (privado)', tag: 'knowledge',
    minRole: 'manager', params: DocVersion, response: Binary, produces: 'application/octet-stream',
  }),
  deleteDocument: route({
    method: 'DELETE', path: '/workspaces/:workspaceId/documents/:documentId', summary: 'Eliminar (sale de la búsqueda; se conserva para auditoría)', tag: 'knowledge',
    minRole: 'admin', params: Doc, response: OkSchema,
  }),

  // ─────────── Shareable assets ───────────
  listAssets: route({
    method: 'GET', path: '/workspaces/:workspaceId/assets', summary: 'Material compartible e interno', tag: 'knowledge',
    minRole: 'advisor', params: Ws, query: K.AssetListQuerySchema, response: items(K.AssetSchema),
  }),
  uploadAsset: route({
    method: 'POST', path: '/workspaces/:workspaceId/assets', summary: 'Subir material (multipart/form-data, campo "file")', tag: 'knowledge',
    minRole: 'admin', params: Ws, body: K.UploadAssetFieldsSchema, response: K.AssetSchema, status: 201,
  }),
  uploadAssetVersion: route({
    method: 'POST', path: '/workspaces/:workspaceId/assets/:assetId/versions', summary: 'Nueva versión del material (reemplaza la anterior)', tag: 'knowledge',
    minRole: 'admin', params: AssetParam, response: K.AssetSchema, status: 201,
  }),
  updateAsset: route({
    method: 'PATCH', path: '/workspaces/:workspaceId/assets/:assetId', summary: 'Editar datos, audiencia o vigencia', tag: 'knowledge',
    minRole: 'admin', params: AssetParam, body: K.UpdateAssetSchema, response: K.AssetSchema,
  }),
  deleteAsset: route({
    method: 'DELETE', path: '/workspaces/:workspaceId/assets/:assetId', summary: 'Eliminar (ya no se puede enviar; se conserva para auditoría)', tag: 'knowledge',
    minRole: 'admin', params: AssetParam, response: OkSchema,
  }),
  downloadAsset: route({
    method: 'GET', path: '/workspaces/:workspaceId/assets/:assetId/download', summary: 'Descarga autorizada de la versión vigente', tag: 'knowledge',
    minRole: 'advisor', params: AssetParam, response: Binary, produces: 'application/octet-stream',
  }),
} as const;

